/**
 * Coach Session Detail page (PR-S1, May 2026).
 *
 * Renders a single session at /dashboard/schedule/:id with edit + cancel +
 * restore affordances. Lives under the admin DashboardLayout — coaches who
 * navigate from /coach/* still see admin sidebar, mirroring the same
 * trade-off PR-X2 made for /coach/teams/:id (consolidating the two layouts
 * is a separate refactor PR).
 *
 * Out of scope (deferred to later PRs):
 *   - Duplicate session            → PR-S2
 *   - Convert practice → match     → PR-S2
 *   - Roster / RSVP / attendance   → PR-S4 (depends on PR-X3a)
 *   - Match lineup (11+1 impact)   → PR-S5 (depends on PR-X3b)
 *
 * Tenant scoping: backend GET /api/coach/sessions/:id 404s on cross-tenant.
 * Friendly "Session not found" page renders for both that AND truly missing
 * ids — same response shape, no existence leak (PR-X1 invariant).
 *
 * Toast: imports from @/hooks/use-toast (the real Toaster mounted in
 * App.tsx). Never @/shared/toast — that's the no-op stub from PR-A's
 * lesson.
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  AlertCircle,
  Edit2,
  XCircle,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import { EditSessionDialog } from "@/components/sessions/EditSessionDialog";

interface SessionDetailData {
  id: number;
  academyId: number | null;
  title: string;
  description: string | null;
  sessionType: string;
  ageGroup: string;
  location: string;
  startTime: string;
  endTime: string;
  coachId: number;
  maxPlayers: number | null;
  status: "scheduled" | "cancelled";
  cancelledAt: string | null;
  cancelledByUserId: number | null;
  createdAt: string;
  updatedAt: string;
}

function statusBadgeColor(status?: string) {
  switch (status) {
    case "cancelled":
      return "bg-red-100 text-red-700 border-red-200";
    case "scheduled":
    default:
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "EEE, d MMM yyyy · h:mm a");
  } catch {
    return iso;
  }
}

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);

  const { data, isLoading, error } = useQuery<{
    success: boolean;
    data: SessionDetailData;
  }>({
    queryKey: ["/api/coach/sessions", id],
    queryFn: async () =>
      apiRequest("GET", `/api/coach/sessions/${id}`) as Promise<any>,
    enabled: !!id,
    retry: false, // 404 fails fast
  });

  const cancelMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", `/api/coach/sessions/${id}/cancel`),
    onSuccess: () => {
      toast({
        title: "Session cancelled",
        description: "Restore from this page if you cancelled by mistake.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/coach/sessions"] });
      setConfirmCancelOpen(false);
    },
    onError: (e: Error) =>
      toast({
        title: "Failed to cancel session",
        description: e.message || "Please try again.",
        variant: "destructive",
      }),
  });

  const restoreMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", `/api/coach/sessions/${id}/restore`),
    onSuccess: () => {
      toast({
        title: "Session restored",
        description: "The session is active again.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/coach/sessions"] });
      setConfirmRestoreOpen(false);
    },
    onError: (e: Error) =>
      toast({
        title: "Failed to restore session",
        description: e.message || "Please try again.",
        variant: "destructive",
      }),
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="session-detail-loading">
        <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
        <div className="h-12 w-2/3 bg-slate-200 rounded animate-pulse" />
        <div className="h-40 w-full bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div
        className="py-12 text-center"
        data-testid="session-detail-not-found"
      >
        <AlertCircle className="h-12 w-12 mx-auto text-slate-400 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Session not found
        </h1>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          This session doesn't exist or isn't part of your academy.
        </p>
        <Button
          onClick={() => navigate("/dashboard/schedule")}
          data-testid="btn-back-to-schedule-from-404"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to schedule
        </Button>
      </div>
    );
  }

  const session = data.data;
  const isCancelled = session.status === "cancelled";

  return (
    <div className="space-y-6">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate("/dashboard/schedule")}
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        data-testid="btn-back-to-schedule"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to schedule
      </button>

      {/* Header — title + status + actions */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight"
              data-testid="session-detail-title"
            >
              {session.title}
            </h1>
            <Badge
              className={cn("border", statusBadgeColor(session.status))}
              data-testid="session-status-badge"
            >
              {isCancelled ? "Cancelled" : "Scheduled"}
            </Badge>
          </div>
          <p className="text-sm text-slate-600">
            {session.sessionType} · {session.ageGroup}
          </p>
          {isCancelled && session.cancelledAt && (
            <p
              className="text-xs text-slate-500 mt-2"
              data-testid="session-cancelled-meta"
            >
              Cancelled {formatDateTime(session.cancelledAt)}
              {session.cancelledByUserId && ` · by user #${session.cancelledByUserId}`}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {!isCancelled && (
            <>
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                disabled={cancelMutation.isPending}
                data-testid="btn-edit-session"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmCancelOpen(true)}
                disabled={cancelMutation.isPending}
                data-testid="btn-cancel-session"
              >
                <XCircle className="h-4 w-4 mr-2" />
                {cancelMutation.isPending ? "Cancelling…" : "Cancel"}
              </Button>
            </>
          )}
          {isCancelled && (
            <Button
              variant="outline"
              onClick={() => setConfirmRestoreOpen(true)}
              disabled={restoreMutation.isPending}
              data-testid="btn-restore-session"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {restoreMutation.isPending ? "Restoring…" : "Restore"}
            </Button>
          )}
        </div>
      </div>

      {/* Info card — present in both scheduled + cancelled views */}
      <div
        className={cn(
          "bg-white rounded-xl shadow-sm border border-slate-200 p-5 transition-opacity",
          isCancelled && "opacity-60",
        )}
        data-testid="session-info-card"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoRow icon={<Calendar className="h-4 w-4 text-slate-400" />} label="Start" value={formatDateTime(session.startTime)} />
          <InfoRow icon={<Clock className="h-4 w-4 text-slate-400" />} label="End" value={formatDateTime(session.endTime)} />
          <InfoRow icon={<MapPin className="h-4 w-4 text-slate-400" />} label="Venue" value={session.location} />
          <InfoRow icon={<User className="h-4 w-4 text-slate-400" />} label="Coach" value={`Coach ID #${session.coachId}`} />
          <InfoRow icon={<Users className="h-4 w-4 text-slate-400" />} label="Capacity" value={session.maxPlayers ? `${session.maxPlayers} players` : "—"} />
        </div>
        {session.description && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 mb-1">Notes</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{session.description}</p>
          </div>
        )}
      </div>

      {/* Future-features placeholders — visible breadcrumbs for the roadmap */}
      <div
        className="bg-slate-50 rounded-xl p-5 border border-dashed border-slate-300"
        data-testid="session-future-placeholders"
      >
        <p className="text-sm text-slate-600">
          <strong>Coming next:</strong> Roster &amp; attendance (PR-S4) and match
          lineup (PR-S5) will surface here when ready.
        </p>
      </div>

      {/* Edit dialog */}
      {editOpen && (
        <EditSessionDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          session={session}
        />
      )}

      {/* Cancel confirmation */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this session?</AlertDialogTitle>
            <AlertDialogDescription>
              The session will be marked cancelled and removed from coaches'
              upcoming view. You can restore it from this page if you change
              your mind. Player notifications will roll out in a later release.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-confirm-cancel-no">
              Keep session
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              data-testid="btn-confirm-cancel-yes"
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling…
                </>
              ) : (
                "Cancel session"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore confirmation */}
      <AlertDialog open={confirmRestoreOpen} onOpenChange={setConfirmRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this session?</AlertDialogTitle>
            <AlertDialogDescription>
              The session will return to scheduled status and reappear on
              upcoming views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-confirm-restore-no">
              Keep cancelled
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
              data-testid="btn-confirm-restore-yes"
            >
              {restoreMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring…
                </>
              ) : (
                "Restore session"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}
